import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import MetricCard from '../../components/ui/metric-card';
import { PhoneCall, BarChart3, Settings, Clock, Activity, TrendingUp, Users } from 'lucide-react';

import LiveCallMonitor from './LiveCallMonitor';
import CallHistoryTable from './CallHistoryTable';
import VoiceConfigPanel from './VoiceConfigPanel';
import VoiceAnalytics from './VoiceAnalytics';
import { useVoiceStats } from '../../hooks/useVoiceAPI';
import { useUiLanguage } from '../../context/UiLanguageContext';
import { useAuth } from '../../context/AuthContext';
import { formatVapiEndedReason } from '../../i18n/vapi-reasons';
import { formatVoiceDuration } from '../../i18n/voice';

const VoiceDashboard = ({ profileId }) => {
    // [UX] Analytics - первая вкладка по умолчанию
    const [activeTab, setActiveTab] = useState('analytics');
    const [days, setDays] = useState(7);
    const [showTest, setShowTest] = useState(false);
    const { lang, t, sklonit } = useUiLanguage();
    const auth = useAuth();
    const user = auth?.user;
    const staffRoles = new Set(['CREATOR', 'SUPERADMIN', 'ADMINISTRATOR', 'MANAGING_PARTNER', 'ATTORNEY', 'PARALEGAL']);
    const isStaff = Boolean(
        user?.is_staff ||
        (user?.roles && user.roles.some((r) => staffRoles.has(typeof r === 'string' ? r : r?.name)))
    );
    const { stats, loading, error } = useVoiceStats(profileId || 'default', days, showTest);

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '24rem', color: 'var(--accent)' }}>
                <div style={{ width: '4rem', height: '4rem', border: '4px solid var(--border)', borderTop: '4px solid var(--accent)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    const mostCommonReason = stats?.hangupAnalysis ?
        (Object.entries(stats.hangupAnalysis).sort((a, b) => b[1] - a[1])[0]?.[0] || 'None')
        : 'None';

    const totalCalls = stats?.totalCalls || 0;
    const avgCost = Number(stats?.avgCostPerCall || 0).toFixed(2);
    const totalCostSubtext = totalCalls > 0
        ? t('voice.dashboard.cost_subtext', { avg: avgCost, calls: sklonit('voice.plural.calls_short', totalCalls) })
        : t('voice.dashboard.cost_zero_subtext');

    const reasonCount = stats?.hangupAnalysis?.[mostCommonReason] || 0;
    const hangupSubtext = mostCommonReason && mostCommonReason !== 'None' && mostCommonReason !== '—'
        ? sklonit('voice.plural.calls_reason', reasonCount)
        : t('voice.dashboard.no_calls');

    const bookedCount = stats?.bookedAppointments ?? 0;
    const conversionSubtext = sklonit('voice.plural.consultations_booked', bookedCount);

    const avgTimeSubtext = sklonit('voice.plural.calls_declined', totalCalls);

    return (
        <div className="w-full" style={{ padding: '0', backgroundColor: 'transparent', minHeight: '100%', color: 'var(--text)' }}>

            {error && (
                <div style={{ backgroundColor: 'var(--danger-soft)', border: '1px solid var(--danger-border, var(--border))', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem', color: 'var(--danger)' }}>
                    {t('voice.dashboard.err_load', { error })}
                </div>
            )}

            {/* Unified Time Window Selector (Controls ALL cards and charts) */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                <div>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--font-display)', margin: 0 }}>
                        {t('voice.dashboard.title')}
                    </h2>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginTop: '0.125rem', margin: 0 }}>
                        {t('voice.dashboard.subtitle', { days })}
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                    {isStaff && (
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', color: 'var(--text-muted)', cursor: 'pointer', userSelect: 'none' }}>
                            <input
                                type="checkbox"
                                data-testid="voice-show-test-toggle"
                                checked={showTest}
                                onChange={(e) => setShowTest(e.target.checked)}
                                style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                            />
                            <span>{lang === 'ru' ? 'Показать тестовые' : 'Show test calls'}</span>
                        </label>
                    )}
                    <div style={{ display: 'inline-flex', backgroundColor: 'var(--surface-sunken)', padding: '0.25rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
                        {[7, 30, 90].map((d) => (
                            <button
                                key={d}
                                type="button"
                                data-testid={`voice-window-${d}`}
                                onClick={() => setDays(d)}
                                style={{
                                    padding: '0.375rem 0.75rem',
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: '0.8125rem',
                                    fontWeight: 500,
                                    border: 'none',
                                    cursor: 'pointer',
                                    backgroundColor: days === d ? 'var(--surface-raised)' : 'transparent',
                                    color: days === d ? 'var(--text)' : 'var(--text-muted)',
                                    boxShadow: days === d ? 'var(--shadow-sm)' : 'none',
                                    transition: 'all 0.15s ease'
                                }}
                            >
                                {t('voice.dashboard.days_btn', { days: d })}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Metric Cards - 4 columns on desktop, 2 on tablet, 1 on mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <MetricCard
                    label={t('voice.metric.total_cost')}
                    value={`$${Number(stats?.totalCost || 0).toFixed(2)}`}
                    subtext={totalCostSubtext}
                    icon={<TrendingUp size={18} />}
                    variant="accent"
                />

                <MetricCard
                    label={t('voice.metric.hangup_analysis')}
                    value={formatVapiEndedReason(mostCommonReason, lang)}
                    subtext={hangupSubtext}
                    icon={<PhoneCall size={18} />}
                    variant="accent"
                />

                <MetricCard
                    label={t('voice.metric.conversion_rate')}
                    value={`${stats?.conversionRate ?? 0}%`}
                    subtext={conversionSubtext}
                    icon={<Activity size={18} />}
                    variant="accent"
                />

                <MetricCard
                    label={t('voice.metric.avg_handle_time')}
                    value={formatVoiceDuration(stats?.avgDuration, t)}
                    subtext={avgTimeSubtext}
                    icon={<Clock size={18} />}
                    variant="accent"
                />
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} style={{ width: '100%' }}>
                <TabsList 
                    className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 p-1.5 w-full mb-6"
                    style={{
                        height: 'auto',
                        backgroundColor: 'var(--surface-sunken)',
                        border: '1px solid var(--border)',
                        borderRadius: '0.75rem'
                    }}
                >
                    <TabsTrigger value="analytics" style={{ width: '100%', padding: '0.625rem 0.75rem', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                        <BarChart3 size={16} /> <span>{t('voice.tab.analytics')}</span>
                    </TabsTrigger>
                    <TabsTrigger value="history" style={{ width: '100%', padding: '0.625rem 0.75rem', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                        <Users size={16} /> <span>{t('voice.tab.history')}</span>
                    </TabsTrigger>
                    <TabsTrigger value="monitor" style={{ width: '100%', padding: '0.625rem 0.75rem', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                        <Activity size={16} /> <span>{t('voice.tab.monitor')}</span>
                    </TabsTrigger>
                    <TabsTrigger value="config" style={{ width: '100%', padding: '0.625rem 0.75rem', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                        <Settings size={16} /> <span>{t('voice.tab.config')}</span>
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="analytics">
                    <Card style={{
                        borderTop: '3px solid var(--accent)',
                        border: '1px solid var(--border)',
                        backgroundColor: 'var(--surface-raised)',
                        borderRadius: '0.75rem',
                        boxShadow: 'var(--shadow-sm)'
                    }}>
                        <CardHeader>
                            <CardTitle style={{ fontSize: '1.25rem', color: 'var(--text)', fontFamily: 'var(--font-display)' }}>{t('voice.dashboard.detailed_analytics')}</CardTitle>
                            <CardDescription style={{ color: 'var(--text-muted)' }}>{t('voice.dashboard.detailed_analytics_sub')}</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <VoiceAnalytics profileId={profileId || 'default'} days={days} showTest={showTest} />
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="history">
                    <Card style={{
                        borderTop: '3px solid var(--accent)',
                        border: '1px solid var(--border)',
                        backgroundColor: 'var(--surface-raised)',
                        borderRadius: '0.75rem',
                        boxShadow: 'var(--shadow-sm)'
                    }}>
                        <CardHeader>
                            <CardTitle style={{ fontSize: '1.25rem', color: 'var(--text)', fontFamily: 'var(--font-display)' }}>Call History</CardTitle>
                            <CardDescription style={{ color: 'var(--text-muted)' }}>Full log of all interactions</CardDescription>
                        </CardHeader>
                        <CardContent style={{ padding: '1rem 1.25rem' }}>
                            <CallHistoryTable profileId={profileId || 'default'} showTest={showTest} />
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="monitor">
                    <Card style={{
                        borderTop: '3px solid var(--accent)',
                        border: '1px solid var(--border)',
                        backgroundColor: 'var(--surface-raised)',
                        borderRadius: '0.75rem',
                        boxShadow: 'var(--shadow-sm)'
                    }}>
                        <CardHeader>
                            <CardTitle style={{ fontSize: '1.25rem', color: 'var(--text)', fontFamily: 'var(--font-display)' }}>Live Call Monitor</CardTitle>
                            <CardDescription style={{ color: 'var(--text-muted)' }}>Real-time monitoring</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <LiveCallMonitor profileId={profileId || 'default'} showTest={showTest} />
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="config">
                    <Card style={{
                        borderTop: '3px solid var(--accent)',
                        border: '1px solid var(--border)',
                        backgroundColor: 'var(--surface-raised)',
                        borderRadius: '0.75rem',
                        boxShadow: 'var(--shadow-sm)'
                    }}>
                        <CardHeader>
                            <CardTitle style={{ fontSize: '1.25rem', color: 'var(--text)', fontFamily: 'var(--font-display)' }}>Configuration</CardTitle>
                            <CardDescription style={{ color: 'var(--text-muted)' }}>Manage settings</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <VoiceConfigPanel profileId={profileId || 'default'} />
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default VoiceDashboard;
